import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addSubscription, addUser } from "../store/slice/userSlice";
import { GET_SUBS_URL } from "../constant/constant";
import { apiCall } from "../API/apiCall";
import useFetch from "./useFetch";
import Store from "../zustand/store";
import { toast } from "react-toastify";

/**
 * Custom React hook for handling user impersonation logic.
 * Encapsulates API calls, local storage updates, and Redux dispatches.
 */
export const useImpersonation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, , , fetchSubsData] = useFetch();
  const { setLoggedUser, setMemberStatus } = Store();
  const [isApiLoading, setIsApiLoading] = useState(false);

  /**
   * Updates user-related data in local storage and dispatches Redux actions.
   * This function does NOT handle the 'impersonating' flag or navigation.
   * @param {object} userData - The user data object to store.
   * @param {string} [message] - Optional toast message to display.
   */
  const updateUserSessionAndLocalStorage = (userData, message) => {
    if (message) toast.success(message);
    localStorage.setItem("LogUser", JSON.stringify(userData.token));
    localStorage.setItem("profileUrl", JSON.stringify(userData.profileUrl));
    localStorage.setItem("useEmail", JSON.stringify(userData.Email));
    localStorage.setItem(
      "subscribe",
      JSON.stringify(userData.SubscriptionType)
    );
    localStorage.setItem("role", JSON.stringify(userData.AdminType));
    localStorage.setItem("Username", JSON.stringify(userData.Username));
    localStorage.setItem("fname", JSON.stringify(userData?.Fname || ""));
    localStorage.setItem("lname", JSON.stringify(userData?.Lname || ""));
    localStorage.setItem("id", JSON.stringify(userData._id));
    localStorage.setItem("userType", userData.userType);

    setLoggedUser(userData);
    dispatch(addUser(userData));
  };

  /**
   * Handles the impersonation process for a given user ID.
   * @param {string} userId - The ID of the user to impersonate.
   */
  const handleImpersonate = async (userId) => {
    console.log(`Attempting to impersonate user with ID: ${userId}`);
    setIsApiLoading(true);

    try {
      // 1. Call impersonate API
      const response = await apiCall({
        url: "/institute/impersonate",
        method: "POST",
        data: { userId },
      });

      // 2. Update user session and local storage with impersonated user data
      updateUserSessionAndLocalStorage(response.user, response.message);
      localStorage.setItem("impersonating", "true");

      // 3. Fetch subscription details for the impersonated user
      const userSubsDetail = await fetchSubsData({
        method: "Post",
        url: GET_SUBS_URL,
        headers: {
          "Content-Type": "application/json",
          authorization: JSON.parse(
            localStorage.getItem("LogUser") || '"mock_admin_token"'
          ),
        },
        body: {
          UserEmail: response.user?.Email,
        },
      });

      // 4. Dispatch subscription details to Redux
      dispatch(addSubscription(userSubsDetail?.subscriptionDetails));

      // 5. Navigate to the institute page
      navigate("/user/dashboard");
    } catch (error) {
      console.error("Impersonation Error:", error);
      //   toast.error("Failed to impersonate user.");
    } finally {
      setIsApiLoading(false);
    }
  };

  /**
   * Handles the process of stopping impersonation.
   */
  const handleStopImpersonating = async () => {
    console.log("Stopping impersonation...");
    setIsApiLoading(true);

    try {
      // 1. Call stop impersonation API
      const response = await apiCall({
        url: "/institute/stop-impersonation",
        method: "POST",
      });

      console.log("Stop Impersonation Response:", response);

      // 2. Update user session and local storage with original admin user data
      updateUserSessionAndLocalStorage(response.user, response.message);
      localStorage.removeItem("impersonating"); // Remove the impersonating flag

      // 3. Clear subscription details (or fetch admin's subscription if applicable)
      dispatch(addSubscription(null));

      // 4. Navigate to the admin management page
      navigate("/admin/manage-institute");
    } catch (error) {
      console.error("Stop Impersonation Error:", error);
      //   toast.error("Failed to stop impersonation.");
    } finally {
      setIsApiLoading(false);
    }
  };

  return { handleImpersonate, handleStopImpersonating, isApiLoading };
};
